/*
  Kalib Watson 9-3-24
  TODO: This component is a temporary component to slowly introduce the Zest VideoPlayer to one location at a time.
  When Zest VideoPlayer has fully replaced the old video player component, this component should replace the TypedVideoModal component and should be renamed.
*/

import React, { Suspense, useState } from "react";
import ReactDOM from "react-dom";
import { VideoPlayer } from "@clarkinc/zest-design-system/ui";
import { cls } from "cls";
import { Modal } from "@clarkinc/zest-design-system";
import useIsMounted from "../useIsMounted";
import { Video } from "../Store/models/Video";
import Environment from "../Environment";
import ZestVideoPlayer from "../videoPlayer/ZestVideoPlayer";

import("./styles.less");

interface Props {
  video?: Video;
  autoPlay?: boolean;
}

const ZestVideoModal = ({ video, autoPlay = true }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMounted = useIsMounted();
  const closeModal = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // Modal will require a Zest update to include a close animation
    Modal.toggle("zestVideoModal");
  };

  const onOpen = () => {
    setIsModalOpen(true);
  };

  const onClose = () => {
    setIsModalOpen(false);
  };

  const isYoutube = video?.youtubeId;

  const baseUrl = Environment.isProduction
    ? "https://cdn.webstaurantstore.com/videos/"
    : "https://assets.dev.webstaurantstore.com/videos/";

  const getSourceUrls = () => {
    if (video?.sources) {
      const { high, medium, low, product, ipad } = video.sources;

      return {
        high: `${baseUrl}${high}`,
        medium: `${baseUrl}${medium}`,
        low: `${baseUrl}${low}`,
        product: `${baseUrl}${product}`,
        ipad: `${baseUrl}${ipad}`,
      };
    }
    return undefined;
  };

  const getCaptionUrl = () => {
    if (video?.closedCaptionFile) {
      const captionBaseUrl = "/videos/closed_captioning/";

      return captionBaseUrl + video.closedCaptionFile;
    }
    return undefined;
  };

  return (
    <>
      {isMounted &&
        ReactDOM.createPortal(
          <Suspense fallback={null}>
            <Modal
              hideHeader
              aria-label="Video Modal"
              className={cls(
                "videoModal",
                "w-full",
                "max-w-5xl",
                "bg-transparent",
                "animate-[slidein_0.25s_ease_normal]",
                "backdrop:animate-[slideinbackdrop_0.25s_ease_normal]",
                // Timeout for the Close button along with the transition property create the slideout animation
                "transition-[all_0.25s_ease]"
              )}
              hideFooter
              id="zestVideoModal"
              contentProps={{
                className: "p-0",
              }}
              onOpen={onOpen}
              onClose={onClose}
            >
              <button
                type="button"
                className="modalclose"
                data-testid="video-modal-close"
                onClick={closeModal}
                style={{ position: "absolute" }}
                aria-label="close video"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-7 w-7"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="4"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              {isYoutube && isModalOpen && (
                <div
                  className="relative w-full pt-[56.25%]"
                  data-testid="youtube-video-container"
                >
                  <ZestVideoPlayer
                    posterImage=""
                    sources={video.sources}
                    currentIndex={0}
                    selected={0}
                    youtubeId={video.youtubeId}
                    isModal
                    autoPlay={autoPlay}
                    playsInline
                  />
                </div>
              )}
              {video && !isYoutube && isModalOpen && (
                <VideoPlayer
                  sources={getSourceUrls()}
                  poster={video.poster}
                  playsInline
                  captionSrc={getCaptionUrl()}
                  theme="wss"
                  crossOrigin="anonymous"
                  autoPlay={autoPlay}
                />
              )}
            </Modal>
          </Suspense>,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          document.querySelector("body") as Element
        )}
    </>
  );
};
export default ZestVideoModal;
